import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import Spinner from './ui/Spinner';
import Badge from './ui/Badge';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

// View-only pentru responsabili — vad declarația + statusul + documentul justificativ.
// Nu mai există aprobare/respingere: doar angajatul semnează. Responsabilii au doar vizibilitate.
const ChangeApprovalModal = ({ changeLogId, onClose }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendMode, setResendMode] = useState(false);
  const [resendForm, setResendForm] = useState({});
  const [resendReason, setResendReason] = useState('');
  const [resending, setResending] = useState(false);

  const initResendForm = (l) => {
    if (!l) return;
    if (l.changeType === 'NAME') {
      setResendForm({ newLastName: l.newValue?.lastName || '' });
    } else {
      const nv = l.newValue || {};
      setResendForm({
        homeAddress: nv.homeAddress || '',
        residenceAddress: nv.residenceAddress || '',
        workAddress: nv.workAddress || '',
        transportMode: (nv.transportMode || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .flatMap((m) => (m === 'mixt' ? ['personal', 'public'] : [m]))
          .join(','),
        transportModeOther: nv.transportModeOther || '',
        routeDetails: nv.routeDetails || '',
        routeDuration: nv.routeDuration != null ? String(nv.routeDuration) : '',
      });
    }
    setResendReason(l.reason || '');
  };

  const handleResend = async () => {
    if (!log) return;
    if (log.changeType === 'NAME' && !resendForm.newLastName?.trim()) {
      showToast(t('changeApproval.errorEnterLastName'), 'error');
      return;
    }
    setResending(true);
    try {
      const fd = new FormData();
      Object.entries(resendForm).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      if (resendReason.trim()) fd.append('reason', resendReason.trim());
      const url =
        log.changeType === 'NAME'
          ? `/employees/${log.employeeId}/change-name`
          : `/employees/${log.employeeId}/change-route`;
      await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast(t('changeApproval.resendSuccess'), 'success');
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || t('changeApproval.resendError'), 'error');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/change-logs/${changeLogId}`)
      .then((res) => {
        if (!cancelled) setLog(res.data);
      })
      .catch((err) => {
        if (!cancelled)
          showToast(err.response?.data?.error || t('changeApproval.loadError'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [changeLogId]);

  const labelType = (type) =>
    type === 'NAME' ? t('changeApproval.typeName') : t('changeApproval.typeRoute');
  const fmt = (log, which) => {
    const v = log[which];
    if (!v) return '—';
    if (log.changeType === 'NAME') {
      return `${v.firstName || ''} ${v.lastName || ''}`.trim() || '—';
    }
    const parts = [];
    if (v.homeAddress) parts.push(`${t('changeApproval.fmtHomeCI')}: ${v.homeAddress}`);
    if (v.residenceAddress)
      parts.push(`${t('changeApproval.fmtResidence')}: ${v.residenceAddress}`);
    if (v.workAddress) parts.push(`${t('changeApproval.fmtWorkplace')}: ${v.workAddress}`);
    if (v.transportMode) {
      const tm = String(v.transportMode)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((c) =>
          c === 'altele'
            ? v.transportModeOther
              ? `${t('changeApproval.transportOther')}: ${v.transportModeOther}`
              : t('changeApproval.transportOther')
            : c
        )
        .join(', ');
      parts.push(`${t('changeApproval.fmtTransport')}: ${tm}`);
    }
    if (v.routeDuration) parts.push(`${t('changeApproval.fmtDuration')}: ${v.routeDuration} min`);
    if (v.routeDetails) parts.push(`${t('changeApproval.fmtDescription')}: ${v.routeDetails}`);
    return parts.length ? parts.join(' · ') : '—';
  };

  const statusBadge = (status) => {
    if (status === 'APPROVED') return { color: 'green', label: t('changeApproval.statusApproved') };
    if (status === 'PENDING_EMPLOYEE_SIGNATURE')
      return { color: 'yellow', label: t('changeApproval.statusPending') };
    if (status === 'REJECTED') return { color: 'red', label: t('changeApproval.statusRejected') };
    return { color: 'gray', label: status };
  };

  if (loading) {
    return (
      <Modal
        open={true}
        onClose={onClose}
        closable={true}
        title={t('changeApproval.declaration')}
        size="lg"
      >
        <Spinner label={t('changeApproval.loadingDetails')} />
      </Modal>
    );
  }
  if (!log) {
    return (
      <Modal
        open={true}
        onClose={onClose}
        closable={true}
        title={t('changeApproval.unavailableTitle')}
        size="md"
        footer={
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <p className="text-sm text-slate-700 dark:text-slate-300">
          {t('changeApproval.unavailableBody')}
        </p>
      </Modal>
    );
  }

  const initiator = log.initiator;
  const initiatorName = initiator
    ? `${initiator.firstName || ''} ${initiator.lastName || ''}`.trim()
    : '—';
  const s = statusBadge(log.status);
  const isInitiator = log.initiatedByUserId === user?.id;
  const canResend = log.status === 'REJECTED' && isInitiator && log.initiatedBy === 'RESPONSIBLE';

  // Sub-view: form retrimitere cu prefil
  if (resendMode) {
    return (
      <Modal
        open={true}
        onClose={() => !resending && setResendMode(false)}
        closable={!resending}
        title={t('changeApproval.resendTitle')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-between w-full">
            <Button variant="secondary" onClick={() => setResendMode(false)} disabled={resending}>
              {t('common.back')}
            </Button>
            <Button
              variant="primary"
              onClick={handleResend}
              loading={resending}
              disabled={resending}
            >
              {t('changeApproval.resend')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('changeApproval.resendIntro')}</p>
          {log.changeType === 'NAME' ? (
            <Input
              label={t('changeApproval.newLastNameLabel')}
              value={resendForm.newLastName || ''}
              onChange={(e) => setResendForm({ ...resendForm, newLastName: e.target.value })}
              placeholder={t('changeApproval.namePlaceholder')}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label={t('changeApproval.homeCILabel')}
                value={resendForm.homeAddress || ''}
                onChange={(e) => setResendForm({ ...resendForm, homeAddress: e.target.value })}
              />
              <Input
                label={t('changeApproval.residenceLabel')}
                value={resendForm.residenceAddress || ''}
                onChange={(e) => setResendForm({ ...resendForm, residenceAddress: e.target.value })}
              />
              <Input
                label={t('changeApproval.workAddressLabel')}
                value={resendForm.workAddress || ''}
                onChange={(e) => setResendForm({ ...resendForm, workAddress: e.target.value })}
              />
              <Input
                label={t('changeApproval.durationLabel')}
                type="number"
                value={resendForm.routeDuration || ''}
                onChange={(e) => setResendForm({ ...resendForm, routeDuration: e.target.value })}
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('changeApproval.transportModeLabel')}{' '}
                  <span className="font-normal text-slate-400">
                    · {t('changeApproval.chooseMultiple')}
                  </span>
                </label>
                {(() => {
                  const sel = resendForm.transportMode
                    ? resendForm.transportMode
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                    : [];
                  const toggle = (val) => {
                    const has = sel.includes(val);
                    const next = has ? sel.filter((m) => m !== val) : [...sel, val];
                    setResendForm({
                      ...resendForm,
                      transportMode: next.join(','),
                      transportModeOther:
                        val === 'altele' && has ? '' : resendForm.transportModeOther,
                    });
                  };
                  const opts = [
                    ['personal', t('changeApproval.transportPersonal')],
                    ['public', t('changeApproval.transportPublic')],
                    ['pe_jos', t('changeApproval.transportWalk')],
                    ['bicicleta', t('changeApproval.transportBike')],
                    ['altele', t('changeApproval.transportOtherOption')],
                  ];
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {opts.map(([v, l]) => (
                        <div key={v}>
                          <label
                            className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer text-sm ${sel.includes(v) ? 'border-accent-500 bg-accent-50 dark:bg-accent-950/40' : 'border-slate-300 dark:border-slate-700'}`}
                          >
                            <input
                              type="checkbox"
                              checked={sel.includes(v)}
                              onChange={() => toggle(v)}
                              className="h-4 w-4 rounded text-accent-600"
                            />
                            {l}
                          </label>
                          {v === 'altele' && sel.includes('altele') && (
                            <input
                              type="text"
                              value={resendForm.transportModeOther || ''}
                              onChange={(e) =>
                                setResendForm({ ...resendForm, transportModeOther: e.target.value })
                              }
                              placeholder={t('changeApproval.transportOtherPlaceholder')}
                              className="w-full mt-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('changeApproval.routeDescLabel')}
                </label>
                <textarea
                  rows={3}
                  value={resendForm.routeDetails || ''}
                  onChange={(e) => setResendForm({ ...resendForm, routeDetails: e.target.value })}
                  className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}
          <Input
            label={t('changeApproval.reasonLabel')}
            value={resendReason}
            onChange={(e) => setResendReason(e.target.value)}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      closable={true}
      title={t('changeApproval.declarationTitle', { type: labelType(log.changeType) })}
      size="lg"
      footer={
        <div className="flex gap-3 justify-between w-full">
          {canResend ? (
            <Button
              variant="primary"
              onClick={() => {
                initResendForm(log);
                setResendMode(true);
              }}
            >
              {t('changeApproval.resendWithCorrections')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
        <div className="flex flex-wrap items-center gap-2">
          <Badge color={s.color}>{s.label}</Badge>
          {log.initiatedBy === 'EMPLOYEE' && (
            <Badge color="gray">{t('changeApproval.initiatedByEmployee')}</Badge>
          )}
          {log.initiatedBy === 'RESPONSIBLE' && (
            <Badge color="gray">{t('changeApproval.initiatedByResponsible')}</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
            <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              {t('changeApproval.previous')}
            </p>
            <p className="whitespace-pre-wrap">{fmt(log, 'oldValue')}</p>
          </div>
          <div className="bg-primary-50 dark:bg-primary-950/30 p-3 rounded-md border border-primary-200 dark:border-primary-800">
            <p className="text-xs uppercase tracking-wider text-primary-700 dark:text-primary-300 mb-1">
              {t('changeApproval.proposedApplied')}
            </p>
            <p className="font-medium text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
              {fmt(log, 'newValue')}
            </p>
          </div>
        </div>

        {log.reason && (
          <p className="text-xs">
            <span className="font-medium">{t('changeApproval.reasonInline')}</span> {log.reason}
          </p>
        )}

        <p className="text-xs text-slate-500 dark:text-slate-400">
          <span className="font-medium">{t('changeApproval.initiatedBy')}</span> {initiatorName}
          {initiator?.role && ` (${initiator.role})`} ·{' '}
          {new Date(log.changedAt).toLocaleString('ro-RO')}
        </p>

        {log.appliedAt && (
          <p className="text-xs text-accent-600 dark:text-accent-400">
            {t('changeApproval.signedAppliedAt', {
              date: new Date(log.appliedAt).toLocaleString('ro-RO'),
            })}
          </p>
        )}

        {/* Anexă semnată — descarcă PDF-ul cu declarația semnată (pe lângă documentul justificativ).
            Disponibilă oricând după inițiere; pentru declarațiile încă nesemnate, anexa apare cu
            mențiunea „În așteptarea semnăturii". */}
        <p className="text-xs">
          <button
            type="button"
            onClick={() => {
              const token = localStorage.getItem('authToken');
              const url = `${api.defaults.baseURL}/change-logs/${log.id}/annex.pdf`;
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((b) => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(b);
                  a.download = `anexa-${log.id}.pdf`;
                  a.click();
                });
            }}
            className="text-primary-600 dark:text-primary-300 hover:underline inline-flex items-center gap-1"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
            {t('changeApproval.downloadSignedAnnex')}
          </button>
        </p>

        {log.supportingDocPath && log.employeeId && (
          <p className="text-xs">
            <button
              type="button"
              onClick={() => {
                const token = localStorage.getItem('authToken');
                const url = `${api.defaults.baseURL}/employees/${log.employeeId}/change-logs/${log.id}/document`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((b) => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(b);
                    a.download = log.supportingDocName || 'document';
                    a.click();
                  });
              }}
              className="text-primary-600 dark:text-primary-300 hover:underline inline-flex items-center gap-1"
            >
              {t('changeApproval.downloadSupportingDoc', { name: log.supportingDocName })}
            </button>
          </p>
        )}

        <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md text-xs">
          <p className="font-semibold mb-1">{t('changeApproval.noteLabel')}</p>
          <p>{t('changeApproval.noteBody')}</p>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeApprovalModal;
